import { ICON__hammer_solid } from '@/assets/icons_fa';
import { UI_PRIMITIVE__context_menu_root, UI_PRIMITIVE__context_menu_trigger } from '@/components/ui/context_menu/UI_PRIMITIVE__context_menu';
import { UI_PRIMITIVE__context_menu_content } from '@/components/ui/context_menu/UI_PRIMITIVE__context_menu_content';
import { UI_PRIMITIVE__context_menu_separator } from '@/components/ui/context_menu/UI_PRIMITIVE__context_menu_separator';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { CONTEXT_MENU_ITEM__image__copy_to_clipboard } from '@/pages/_base/image/CONTEXT_MENU_ITEM__image__copy_to_clipboard';
import { CONTEXT_MENU_ITEM__image__download } from '@/pages/_base/image/CONTEXT_MENU_ITEM__image__download';
import { CONTEXT_MENU_ITEM__image__paste_from_clipboard_to_field } from '@/pages/_base/image/CONTEXT_MENU_ITEM__image__paste_from_clipboard_to_field';
import { CONTEXT_MENU_ITEM__image__paste_from_clipboard_to_field_list } from '@/pages/_base/image/CONTEXT_MENU_ITEM__image__paste_from_clipboard_to_field_list';
import { CONTEXT_MENU_ITEM__image__show_source } from '@/pages/_base/image/CONTEXT_MENU_ITEM__image__show_source';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD__list_image_asset } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_image_asset';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { FN_GENERATION_TOOL__create_for_field_consumer } from '@shared/dto/generation/graph_invoke/_templates/FN_GENERATION_TOOL__create_for_field_consumer';
import { CONTEXT_MENU_ITEM__field_info_hash } from './general/CONTEXT_MENU_ITEM__field_info_hash';
import { CONTEXT_MENU_ITEM__field_info_id } from './general/CONTEXT_MENU_ITEM__field_info_id';
import { CONTEXT_MENU_ITEM__field_locate } from './general/CONTEXT_MENU_ITEM__field_locate';
import { CONTEXT_MENU_ITEM__field_image__edit_with_tool } from './image/CONTEXT_MENU_ITEM__field_image__edit_with_tool';

interface PROPS {
  field: FIELD__base;
  children: React.ReactNode;
}

export function G_CONTEXT_MENU__field({ field, children }: PROPS) {
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */

  let { graph } = generation_current.tool;

  /*
   * RENDERS
   */

  function render_items_general() {
    return (
      <>
        <CONTEXT_MENU_ITEM__field_locate {...{ field }} />
      </>
    );
  }

  function render_items_image_list() {
    if (!(field instanceof FIELD__list_image_asset)) return null;

    return (
      <>
        <CONTEXT_MENU_ITEM__image__paste_from_clipboard_to_field_list field={field} />
      </>
    );
  }
  function render_items_image() {
    if (!(field instanceof FIELD__image_asset)) return null;

    return (
      <>
        <CONTEXT_MENU_ITEM__image__show_source image_asset={field.asset} />
        <CONTEXT_MENU_ITEM__image__copy_to_clipboard image_asset={field.asset} />
        <CONTEXT_MENU_ITEM__image__paste_from_clipboard_to_field field={field} />
        <CONTEXT_MENU_ITEM__image__download image_asset={field.asset} />
        {render_items_image_image(field)}
      </>
    );
  }

  function render_items_image_image(field: FIELD__image_asset) {
    if (!field.is_ready_for_manual_edit()) return null;

    let generation_tool = FN_GENERATION_TOOL__create_for_field_consumer(field, graph);

    if (generation_tool.is_stub()) return null;

    let out = (
      <CONTEXT_MENU_ITEM__field_image__edit_with_tool
        key={`fn__graph_create_${generation_tool.name}`}
        field_consumer={field}
        Icon={ICON__hammer_solid}
        {...{ generation_tool }}
      />
    );

    return (
      <>
        <UI_PRIMITIVE__context_menu_separator />
        {out}
      </>
    );
  }

  function render_items_info() {
    return (
      <>
        <UI_PRIMITIVE__context_menu_separator />
        <CONTEXT_MENU_ITEM__field_info_id {...{ field }} />
        <CONTEXT_MENU_ITEM__field_info_hash {...{ field }} />
      </>
    );
  }

  function render_with_context_menu(out: React.ReactNode) {
    return (
      <UI_PRIMITIVE__context_menu_root>
        <UI_PRIMITIVE__context_menu_trigger>{out}</UI_PRIMITIVE__context_menu_trigger>

        <UI_PRIMITIVE__context_menu_content className="flex flex-col gap-1">
          {render_items_general()}

          {render_items_image()}
          {render_items_image_list()}

          {render_items_info()}
        </UI_PRIMITIVE__context_menu_content>
      </UI_PRIMITIVE__context_menu_root>
    );
  }

  /*
   * HTML
   */
  if (generation_current.is_stub()) return children;
  if (generation_current._id != field.meta.graph_id) return children;

  return render_with_context_menu(children);
}
